<template>
  <div class="url-input" data-testid="http-url-input">
    <a-form layout="vertical" @submit.prevent="handleSubmit" data-testid="http-url-form">
      <a-form-item
        label="URLs to Check"
        :help="`Enter one URL per line (max ${maxUrls} URLs)`"
        data-testid="http-url-form-item"
      >
        <a-textarea
          v-model:value="localUrls"
          :rows="8"
          placeholder="https://example.com&#10;https://another-site.com&#10;https://test.org"
          :disabled="disabled"
          :max-length="maxUrls * 200"
          data-testid="http-url-textarea"
        />
        <div class="url-count" data-testid="http-url-count">
          <span :class="{ 'count-warning': urlCount > maxUrls }">
            {{ urlCount }} URL{{ urlCount !== 1 ? 's' : '' }}
            {{ urlCount > maxUrls ? `(max ${maxUrls})` : '' }}
          </span>
        </div>
      </a-form-item>
      
      <a-row :gutter="16">
        
        <a-col :xs="24" :sm="12" style="margin-bottom: 8px;">
          <a-button
            size="large"
            :disabled="disabled || localUrls.length === 0"
            block
            @click="handleClear"
            data-testid="http-clear-button"
          >
            Clear
          </a-button>
        </a-col>
        <a-col :xs="24" :sm="12">
          <a-button
            type="primary"
            html-type="submit"
            size="large"
            :loading="loading"
            :disabled="disabled || urlCount === 0 || urlCount > maxUrls"
            block
            data-testid="http-check-button"
          >
            <template #icon>
              <search-outlined />
            </template>
            Check URLs
          </a-button>
        </a-col>
      </a-row>
    </a-form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { SearchOutlined } from '@ant-design/icons-vue'

interface Props {
  modelValue: string
  loading?: boolean
  disabled?: boolean
  maxUrls?: number
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'submit', urls: string[]): void
  (e: 'clear'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
  maxUrls: 50
})

const emit = defineEmits<Emits>()

const localUrls = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
})

const urlCount = computed(() => {
  const urls = localUrls.value.split('\n').filter(url => url.trim().length > 0)
  return urls.length
})

const handleSubmit = () => {
  const urls = localUrls.value
    .split('\n')
    .map(url => url.trim())
    .filter(url => url.length > 0)
  
  if (urls.length > 0 && urls.length <= props.maxUrls) {
    emit('submit', urls)
  }
}

const handleClear = () => {
  emit('update:modelValue', '')
  emit('clear')
}
</script>

<style scoped lang="scss">
.url-input {
  .url-count {
    margin-top: 8px;
    text-align: right;
    font-size: 12px;
    color: #666;
    
    .count-warning {
      color: #ff4d4f;
      font-weight: 600;
    }
  }
}
</style>
